import { supabase } from '../supabase';
import useStore from '../store/useStore';
import * as db from '../db';
import { mapDbStudent } from '../utils/helpers';
import { useFeeStore } from '../pages/Fees/store/useFeeStore';

// Every localStorage key here is unscoped by user or school — it survives
// account switches inside the same browser, and a platform-admin switching
// into a different school. Supabase's own session key (sb-*) is preserved;
// everything else is wiped so nothing from the previous tenant leaks onto
// screen (a stale dashboard/payroll/exam figure from the account or school
// just left).
export function clearTenantLocalCache() {
  try {
    Object.keys(localStorage).forEach((key) => {
      if (!key.startsWith('sb-')) localStorage.removeItem(key);
    });
  } catch (err) {
    console.warn('Failed to clear tenant-scoped local cache', err);
  }
}

// Module-level (not component-level) dedup guards — this needs to be
// callable from outside the App component (e.g. the superadmin school
// switcher in Topbar), not just the auth-state-change listener.
const loadState = { loading: false, loadedUserId: null };

// Called on sign-out so the next login always runs a full load.
export function resetLoadState() {
  loadState.loading = false;
  loadState.loadedUserId = null;
}

// Everything held in memory for the person who is leaving: the main store and the fee store.
export function clearSessionData() {
  useStore.getState().resetSessionData();
  useFeeStore.getState().reset();
}

// The account or its role could not be read: end the session and say so on the login screen.
// Nothing is guessed: an unknown role gets no pages and no permissions.
async function rejectSession(notice = 'We could not load your account. Please sign in again.') {
  useStore.getState().setLoginNotice(notice);
  clearSessionData();
  resetLoadState();
  useStore.getState().setDbLoading(false);
  await supabase.auth.signOut();
}

export async function loadStudentsData(schoolId) {
  try {
    // Each role loads only what its permissions let it open. hasPermission already works here:
    // loadUserData calls setUser before it calls this function.
    const has = useStore.getState().hasPermission;
    const needAccounts = has('expenses.view') || has('fees.view') || has('pos.view');
    const needFees = has('fees.view') || has('students.add') || has('students.edit');
    const needProducts = has('pos.view') || has('inventory.view');
    const studentCols = has('fees.view')
      ? '*, families(*), classes(name), sections(name), student_fee_assignments(fee_head_id, amount, is_active, discount_type, discount_value)'
      : '*, families(*), classes(name), sections(name)';
    const skip = (empty) => Promise.resolve(empty);
    const [studentsResult, accs, feeHeads, classes, dbSettings, feeStructures, prods, stock] = await Promise.all([
      supabase
        .from('students')
        .select(studentCols)
        .eq('school_id', schoolId)
        .order('name'),
      needAccounts ? db.getAccounts(schoolId) : skip([]),
      needFees ? db.getFeeHeads(schoolId) : skip([]),
      db.getClasses(schoolId),
      db.getSettings(schoolId),
      needFees ? db.getFeeStructures(schoolId) : skip({}),
      needProducts ? db.getProducts(schoolId) : skip([]),
      needProducts ? db.getProductStock(schoolId) : skip({}),
    ]);
    if (studentsResult.data) useStore.getState().setStudents(studentsResult.data.map(mapDbStudent));
    useStore.getState().setAccounts(accs);
    useStore.getState().updateSettings({ feeHeads });
    useStore.getState().setClasses(classes);
    useStore.getState().setFeeStructures(feeStructures);
    useStore.getState().setProducts(prods);
    useStore.getState().setProductStock(stock);
    db.runOpportunisticCleanup();
    // Never auto-write local settings into the DB — a transient read failure
    // or a genuinely empty settings table would silently overwrite the
    // school's real settings with a stale device-local copy. If empty, the
    // UI just shows the built-in defaults until someone saves via Settings.
    if (Object.keys(dbSettings).length > 0) {
      useStore.getState().loadSettings(dbSettings);
    }
  } catch (err) {
    console.error('Failed to load students', err);
  }
}

// force: true skips the "already loaded this exact user" dedup — needed for
// a superadmin school switch, where authUser.id is unchanged but school_id
// and permissions have moved under it.
export async function loadUserData(authUser, { force = false } = {}) {
  if (!force) {
    if (useStore.getState().user?.id === authUser.id && !useStore.getState().dbLoading) {
      return; // already loaded this exact user, not mid-load — do nothing
    }
    if (loadState.loading) return;
    // Skip only if that user is genuinely still in the store. After a sign-out the
    // store user is null, so an immediate re-login must load again.
    if (loadState.loadedUserId === authUser.id && useStore.getState().user?.id === authUser.id) return;
  }

  let lastAuthUserId = null;
  try { lastAuthUserId = localStorage.getItem('lastAuthUserId'); } catch {}
  if (lastAuthUserId !== authUser.id) {
    // null !== a real id too — first run after this patch, or any
    // browser state we can't attribute to the incoming user, clears
    // by default rather than assuming it's safe to keep.
    clearTenantLocalCache();
  }
  try { localStorage.setItem('lastAuthUserId', authUser.id); } catch {}

  // Someone else was signed in on this device: drop what that user loaded before this one's data arrives.
  const previousUser = useStore.getState().user;
  if (previousUser && previousUser.id !== authUser.id) clearSessionData();

  loadState.loading = true;
  useStore.getState().setDbLoading(true);
  // Priority fix: always cache schoolId so refresh never loses students
  const getCachedSchoolId = () => {
    try { return window._schoolId || localStorage.getItem('schoolId'); } catch { return null; }
  };
  const cacheSchoolId = (id) => {
    if (!id) return;
    window._schoolId = id;
    try { localStorage.setItem('schoolId', id); } catch {}
  };
  try {
    // The role decides what this person may open, so it has to be known. One retry, then the session ends:
    // an account whose role cannot be read gets nothing, never an admin default.
    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('*, roles(name, permissions), password_changed')
        .eq('id', authUser.id)
        .single();
      return !error && data?.roles?.name ? data : null;
    };
    const profile = (await fetchProfile()) || (await fetchProfile());
    if (!profile) { await rejectSession(); return; }
    if (profile.access_expires_at && new Date(profile.access_expires_at) <= new Date()) {
      await rejectSession('This access has ended. Please contact EdEase for a new login.');
      return;
    }
    if (profile.is_active === false) {
      await rejectSession('This account has been deactivated. Please contact your school admin.');
      return;
    }

    // window._schoolId is written here and nowhere else; it always equals user.schoolId after load.
    const schoolId = profile?.school_id || getCachedSchoolId();
    cacheSchoolId(schoolId);
    useStore.getState().hydrateForSchool(schoolId);

    const { data: staffLink } = await supabase
      .from('staff')
      .select('id')
      .eq('user_id', authUser.id)
      .maybeSingle();

    // Platform admins get every permission in the UI, in any school —
    // has_permission() already returns true for them in the DB regardless
    // of that school's admin-role configuration. Loaded here (not only when
    // Topbar mounts) so it's set at login and survives a school switch.
    let isPlatformAdmin = false;
    try {
      const { data, error } = await supabase.rpc('is_platform_admin');
      if (error) console.error('is_platform_admin failed', error);
      else isPlatformAdmin = !!data;
    } catch (err) {
      console.error('is_platform_admin threw', err);
    }

    // Modules this school has. null = every module; a failed read also means every module (hiding is cosmetic).
    let enabledModules = null;
    try {
      const { data: schoolRow, error: modErr } = await supabase.from('schools').select('enabled_modules').eq('id', schoolId).maybeSingle();
      if (modErr) console.error('enabled_modules read failed', modErr);
      enabledModules = schoolRow?.enabled_modules ?? null;
    } catch (err) {
      console.error('enabled_modules read failed', err);
    }

    const roleName = profile.roles.name;

    useStore.getState().setUser({
      id: authUser.id,
      email: authUser.email,
      name: profile?.name || authUser.email?.split('@')[0] || 'Admin',
      roleName,
      permissions: profile.roles.permissions || {},
      staffId: staffLink?.id || null,
      schoolId,
      isPlatformAdmin,
      enabledModules,
      accessExpiresAt: profile?.access_expires_at ?? null,
      passwordChanged: profile?.password_changed ?? null,
    });

    // Parents/students route to ParentPortal, which never needs staff data
    // (students, fee heads, accounts, products) and can't read the
    // staff-only settings table under RLS — loading it for them just
    // produces failed 403 writes on every login.
    if (schoolId && roleName !== 'parent' && roleName !== 'student') await loadStudentsData(schoolId);
  } catch (err) {
    // Never fall back to a guessed or "unverified" user: end the session and ask for a new sign-in.
    console.error('loadUserData failed', err);
    await rejectSession();
    return;
  }
  loadState.loadedUserId = authUser.id;
  loadState.loading = false;
  useStore.getState().setDbLoading(false);
}
